import { FormatNumberOptions, IntlShape } from 'react-intl';

import { COUNTRY_CODES } from './constants';

export const getCurrencyCodeFromCountryCode = (
  countryCode?: string
): string => {
  switch (countryCode) {
    case COUNTRY_CODES.VN:
      return 'VND';
    case COUNTRY_CODES.SG:
      return 'SGD';
    case COUNTRY_CODES.ID:
      return 'IDR';
    case COUNTRY_CODES.MY:
      return 'MYR';
    default:
      return 'IDR';
  }
};

export const getCurrencyCode = (
  salaries: { CurrencyCode: string }[] | undefined,
  countryCode: string | undefined
): string => {
  if (salaries && salaries?.length > 0) {
    return salaries[0].CurrencyCode;
  }

  return getCurrencyCodeFromCountryCode(countryCode);
};

export const getLocaleFromCurrencyCode = (
  defaultLocale: string,
  currencyCode: string
): string => {
  if (defaultLocale.startsWith('en')) {
    return defaultLocale;
  }
  if (currencyCode === 'IDR') {
    return 'id-ID';
  } else if (currencyCode === 'VND') {
    return 'vi-VN';
  }

  return defaultLocale;
};

const EN_IDR_COMPACT_MAP: Record<string, string> = { M: 'Mio' };

const replaceEnglishCompact = (
  formatted: string,
  locale: string,
  currencyCode: string
): string => {
  if (!locale.startsWith('en') || currencyCode !== 'IDR') return formatted;
  return formatted.replace(/M\b/g, m => EN_IDR_COMPACT_MAP[m] ?? m);
};

export const formatCurrency = (
  value: number,
  locale: string,
  currencyCode: string,
  maximumFractionDigits?: number
) =>
  replaceEnglishCompact(
    Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      ...(maximumFractionDigits !== undefined && { maximumFractionDigits }),
      notation: 'compact',
    }).format(Number(value)),
    locale,
    currencyCode
  );

export const formatCurrencyRange = (
  min: number,
  max: number,
  locale: string,
  currencyCode: string,
  maximumFractionDigits?: number
) => {
  const formatter = Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    ...(maximumFractionDigits !== undefined && { maximumFractionDigits }),
    notation: 'compact',
  });
  const [low, high] = min <= max ? [min, max] : [max, min];
  const lowParts = formatter.formatToParts(low);
  const highParts = formatter.formatToParts(high);
  const partValue = (parts: Intl.NumberFormatPart[], type: string) =>
    parts.find(p => p.type === type)?.value ?? '';
  const compact = partValue(highParts, 'compact');
  if (compact !== partValue(lowParts, 'compact')) {
    return `${replaceEnglishCompact(
      formatter.format(low),
      locale,
      currencyCode
    )} - ${replaceEnglishCompact(
      formatter.format(high),
      locale,
      currencyCode
    )}`;
  }
  const numericValue = (parts: Intl.NumberFormatPart[]) =>
    parts
      .filter(
        p =>
          p.type !== 'currency' && p.type !== 'compact' && p.type !== 'literal'
      )
      .map(p => p.value)
      .join('');
  const displayCompact = replaceEnglishCompact(compact, locale, currencyCode);
  return `${partValue(highParts, 'currency')} ${numericValue(
    lowParts
  )}-${numericValue(highParts)}${displayCompact}`;
};

type TalentSalary = {
  expectation: number;
  maxExpectation: number;
  currencyCode: string;
};

export const formatTalentSalary = (
  value: number | null | undefined,
  currencyCode: string | null | undefined,
  locale: string
): string | null => {
  if (!value || !currencyCode) return null;
  return formatCurrency(Number(value), locale, currencyCode);
};

export const formatTalentSalaryRange = (
  salary: TalentSalary,
  locale: string
): string | null => {
  if (!salary.currencyCode) return null;
  const min = Number(salary.expectation) || 0;
  const max = Number(salary.maxExpectation) || 0;
  if (!min && !max) return null;
  if (min && max && min !== max) {
    return formatCurrencyRange(min, max, locale, salary.currencyCode);
  }
  return formatCurrency(min || max, locale, salary.currencyCode);
};

export const intlFormatCurrencyValue = (
  intl: IntlShape,
  value: string | number,
  currency: string,
  formattingOptions?: FormatNumberOptions
) =>
  intl.formatNumber(Number(value), {
    currency,
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    ...formattingOptions,
  });
